import { motion } from "framer-motion";
import { MapPin, Clock, Banknote } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export const JobsSection = ({ jobs, settings }) => {
  const waNumber = (settings.whatsapp || "").replace(/\D/g, "").replace(/^0/, "44");
  return (
    <section id="jobs" className="py-20 sm:py-24 bg-slate-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-blue-600 mb-4">Careers</p>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <h2 className="text-4xl sm:text-5xl tracking-tight font-semibold text-slate-900">Join our delivery crew</h2>
          <p className="text-base text-slate-600 max-w-sm">New driver and crew roles added regularly across Bristol and the South West. Apply in minutes.</p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="jobs-grid">
          {jobs.length === 0 && (
            <p className="text-slate-500 col-span-full" data-testid="jobs-empty-state">No open roles right now — check back soon or message us on WhatsApp.</p>
          )}
          {jobs.map((job, i) => (
            <motion.div key={job.id || i} data-testid={`job-card-${i}`}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              className="bg-white border border-slate-200 rounded-lg p-6 flex flex-col shadow-[0_2px_8px_rgba(15,23,42,0.04)] hover:shadow-[0_8px_24px_rgba(15,23,42,0.08)] hover:-translate-y-1 transition-all duration-300">
              <div className="flex flex-wrap gap-2 mb-4">
                {(job.tags || []).map((t) => (
                  <Badge key={t} className="bg-cyan-50 text-cyan-700 hover:bg-cyan-50 border border-cyan-200 rounded-md">{t}</Badge>
                ))}
              </div>
              <h3 className="text-xl font-medium text-slate-900" data-testid={`job-title-${i}`}>{job.title}</h3>
              <div className="mt-3 space-y-2 text-sm text-slate-600">
                <p className="flex items-center gap-2"><MapPin size={15} className="text-blue-600" /> {job.location}</p>
                <p className="flex items-center gap-2"><Clock size={15} className="text-blue-600" /> {job.job_type}</p>
                {job.pay_rate && <p className="flex items-center gap-2"><Banknote size={15} className="text-blue-600" /> {job.pay_rate}</p>}
              </div>
              {job.description && <p className="mt-4 text-sm text-slate-500 leading-relaxed line-clamp-3">{job.description}</p>}
              <Button asChild data-testid={`job-apply-btn-${i}`} className="mt-6 bg-blue-600 hover:bg-blue-700 text-white rounded-md w-full">
                <a href={`https://wa.me/${waNumber}?text=${encodeURIComponent(`Hi PRGL, I'd like to apply for the ${job.title} role in ${job.location}.`)}`}
                  target="_blank" rel="noopener noreferrer">Apply Now</a>
              </Button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
